'use client'

import React from 'react'
import Image from 'next/image'
import { useTheme } from '../theme/provider'
import { ArticleCard, Article } from './article-card'
import { Breadcrumb, BreadcrumbItem } from './breadcrumb'

export interface ArticleDetailPageProps {
  /**
   * Article title
   */
  title: string
  /**
   * Article hero image URL
   */
  heroImage: string
  /**
   * Article category
   */
  category: string
  /**
   * Published date
   */
  date: string
  /**
   * Article author
   */
  author?: string
  /**
   * Article content (HTML or React nodes)
   */
  content: React.ReactNode
  /**
   * Breadcrumb items for navigation
   */
  breadcrumbItems: BreadcrumbItem[]
  /**
   * Related articles to display in sidebar
   */
  relatedArticles?: Article[]
  /**
   * Function to get article URL
   */
  getArticleUrl: (article: Article) => string
  /**
   * Optional: Function to optimize image URLs
   */
  getOptimizedImageUrl?: (url: string, width?: number, quality?: number) => string
  /**
   * Optional: Function to get category label
   */
  getCategoryLabel?: (article: Article, fallbackIndex?: number) => string
  /**
   * Optional: Function to format date
   */
  formatDate?: (date: string) => string
  /**
   * Optional: Function to strip HTML
   */
  stripHtml?: (html: string) => string
  /**
   * Locale for localization
   */
  locale?: string
  /**
   * Base URL for breadcrumb schema
   */
  baseUrl?: string
  /**
   * Section titles
   */
  sectionTitles?: {
    relatedArticles?: string
  }
}

/**
 * ArticleDetailPage Template - Real Madrid Style
 *
 * Full article detail page layout:
 * - Breadcrumb navigation
 * - Hero image with gradient overlay (LCP OPTIMIZED)
 * - Article metadata bar (category, date, author)
 * - Large article title (h1)
 * - Two-column layout: Article body (8/12) + Related articles sidebar (4/12)
 * - Responsive design with mobile-first approach
 * - Max width container: 1440px
 *
 * LCP (Largest Contentful Paint) Optimizations:
 * - Hero image uses priority={true} for immediate loading
 * - Defined aspect ratio (16:9) prevents layout shift
 * - Optimized sizes attribute for responsive images
 * - Quality set to 90 for hero (balance between quality & performance)
 * - Blur placeholder for progressive loading
 * - Image container matches content width (1440px max)
 * - Beautiful gradient background reduces perceived loading time
 *
 * Hero Image Design:
 * - Constrained width matching content (not full-width)
 * - Rounded corners (3xl) for modern look
 * - Shadow effects for depth
 * - Gradient background with radial accent
 * - Decorative border with subtle glow
 * - 16:9 aspect ratio for consistency
 *
 * SEO Best Practices:
 * - Proper semantic HTML (<article>, <time>, <h1>)
 * - Optimized image alt text
 * - Breadcrumb schema support
 * - Related articles for internal linking
 *
 * NOTE: This component should be wrapped with PageLayout component to ensure
 * consistent header/footer spacing across all pages.
 *
 * @example
 * ```tsx
 * <PageLayout header={headerProps} footer={footerProps}>
 *   <ArticleDetailPage
 *     title="Champions League Victory"
 *     heroImage="/image.jpg"
 *     category="Match Report"
 *     date="2024-06-10"
 *     content={<div>Article content...</div>}
 *     breadcrumbItems={breadcrumbs}
 *     relatedArticles={articles}
 *     getArticleUrl={(article) => `/news/${article.id}`}
 *     getOptimizedImageUrl={(url, width, quality) => `${url}?w=${width}&q=${quality}`}
 *     locale="en"
 *   />
 * </PageLayout>
 * ```
 *
 * For optimal LCP, ensure your page/layout preloads the hero image:
 * ```tsx
 * <head>
 *   <link
 *     rel="preload"
 *     as="image"
 *     href={heroImage}
 *     imageSrcSet="..."
 *     imageSizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 1376px"
 *   />
 * </head>
 * ```
 */
export function ArticleDetailPage({
  title,
  heroImage,
  category,
  date,
  author,
  content,
  breadcrumbItems,
  relatedArticles = [],
  getArticleUrl,
  getOptimizedImageUrl,
  getCategoryLabel,
  formatDate,
  stripHtml,
  locale = 'en',
  baseUrl,
  sectionTitles = {
    relatedArticles: 'Related Articles',
  },
}: ArticleDetailPageProps) {
  const { theme } = useTheme()

  const optimizedHeroImage = getOptimizedImageUrl
    ? getOptimizedImageUrl(heroImage, 1200, 85)
    : heroImage

  const formattedDate = formatDate ? formatDate(date) : new Date(date).toLocaleDateString()

  return (
    <div
      className="pb-16 transition-colors duration-300"
      style={{ backgroundColor: theme.colors.background }}
    >
      <article>
        {/* Breadcrumb Navigation */}
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <Breadcrumb
            items={breadcrumbItems}
            locale={locale}
            baseUrl={baseUrl}
          />
        </div>

        {/* Main Grid Container - Hero + Content + Sidebar */}
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column - Hero Image + Article Content */}
            <div className={relatedArticles.length > 0 ? 'lg:col-span-8' : 'lg:col-span-12'}>
              {/* Hero Image Section - Optimized for LCP */}
              <div
                className="w-full py-8 md:py-12 -mx-4 sm:-mx-6 lg:mx-0"
                style={{
                  background: `linear-gradient(135deg, ${theme.colors.background} 0%, ${theme.colors.surface || '#f8f9fa'} 50%, ${theme.colors.background} 100%)`,
                }}
              >
                <div className="px-4 sm:px-6 lg:px-0">
                  {/* Hero Image Container with aspect ratio preservation */}
                  <div
                    className="relative w-full rounded-3xl overflow-hidden shadow-2xl"
                    style={{
                      aspectRatio: '16/9',
                      background: `radial-gradient(circle at 30% 50%, ${theme.colors.primaryLight || theme.colors.primary}15 0%, transparent 50%)`,
                    }}
                  >
                    {/* Main Hero Image - LCP Optimized */}
                    <Image
                      src={optimizedHeroImage}
                      alt={title}
                      fill
                      className="object-cover"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 90vw, 896px"
                      priority={true}
                      quality={90}
                      placeholder="blur"
                      blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAb/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
                    />

                    {/* Gradient Overlay for better text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

                    {/* Decorative Border Effect */}
                    <div
                      className="absolute inset-0 rounded-3xl"
                      style={{
                        border: `1px solid ${theme.colors.border}20`,
                        boxShadow: `inset 0 1px 0 0 rgba(255, 255, 255, 0.1)`,
                      }}
                    />

                    {/* Category Badge - Positioned on image */}
                    <div className="absolute top-6 left-6 z-10">
                      <span
                        className="inline-block px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-lg backdrop-blur-md shadow-lg"
                        style={{
                          backgroundColor: `${theme.colors.primary}`,
                          color: '#ffffff',
                        }}
                      >
                        {category}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Article Metadata */}
              <div className="flex flex-wrap items-center gap-3 mb-6 text-sm mt-8">
                <span
                  className="font-semibold uppercase tracking-wide"
                  style={{ color: theme.colors.primary }}
                >
                  {category}
                </span>
                <span
                  className="w-1 h-1 rounded-full"
                  style={{ backgroundColor: theme.colors.secondary || theme.colors.primary }}
                />
                <time
                  dateTime={date}
                  style={{ color: theme.colors.textSecondary }}
                >
                  {formattedDate}
                </time>
                {author && (
                  <>
                    <span
                      className="w-1 h-1 rounded-full"
                      style={{ backgroundColor: theme.colors.secondary || theme.colors.primary }}
                    />
                    <span style={{ color: theme.colors.textSecondary }}>
                      By {author}
                    </span>
                  </>
                )}
              </div>

              {/* Article Title */}
              <h1
                className="text-4xl md:text-5xl font-black mb-8 leading-tight"
                style={{ color: theme.colors.text }}
              >
                {title}
              </h1>

              {/* Article Body */}
              <div
                className="prose prose-lg max-w-none article-content"
                style={{
                  color: theme.colors.text,
                }}
              >
                {content}
              </div>

              {/* Article Footer / Share / Tags could go here */}
            </div>

            {/* Right Column - Sidebar spanning entire page height */}
            {relatedArticles.length > 0 && (
              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-24">
                  <div
                    className="rounded-xl p-6 shadow-md"
                    style={{
                      backgroundColor: theme.colors.surface,
                      borderColor: theme.colors.border,
                    }}
                  >
                    <h2
                      className="text-xl font-bold mb-6"
                      style={{ color: theme.colors.text }}
                    >
                      {sectionTitles.relatedArticles}
                    </h2>

                    <div className="space-y-6">
                      {relatedArticles.slice(0, 4).map((article, index) => (
                        <div key={article.id}>
                          {/* Compact Article Card for Sidebar */}
                          <a
                            href={getArticleUrl(article)}
                            className="group block"
                          >
                            <div className="flex gap-4">
                              {/* Thumbnail */}
                              <div className="relative w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden">
                                <Image
                                  src={article.jetpack_featured_media_url || '/placeholder.jpg'}
                                  alt={stripHtml ? stripHtml(article.title.rendered) : article.title.rendered}
                                  fill
                                  className="object-cover group-hover:scale-105 transition-transform duration-300"
                                  sizes="96px"
                                  loading="lazy"
                                />
                              </div>

                              {/* Text */}
                              <div className="flex-1 min-w-0">
                                <h3
                                  className="font-bold text-sm line-clamp-2 mb-2 transition-colors"
                                  style={{ color: theme.colors.text }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.color = theme.colors.primary
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.color = theme.colors.text || '#000000'
                                  }}
                                >
                                  {stripHtml ? stripHtml(article.title.rendered) : article.title.rendered}
                                </h3>
                                <p
                                  className="text-xs"
                                  style={{ color: theme.colors.textSecondary }}
                                >
                                  {formatDate ? formatDate(article.date) : new Date(article.date).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </a>

                          {/* Divider between items */}
                          {index < relatedArticles.slice(0, 4).length - 1 && (
                            <div
                              className="h-px mt-6"
                              style={{
                                background: `linear-gradient(to right, transparent, ${theme.colors.divider || theme.colors.border}, transparent)`,
                              }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            )}
          </div>
        </div>
      </article>

      {/* Additional Related Articles Grid (below main content) - Full Width */}
      {relatedArticles.length > 4 && (
        <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <h2
            className="text-2xl font-bold mb-6"
            style={{ color: theme.colors.text }}
          >
            More Articles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedArticles.slice(4, 7).map((article, index) => (
              <ArticleCard
                key={article.id}
                article={article}
                index={index}
                getArticleUrl={getArticleUrl}
                getOptimizedImageUrl={getOptimizedImageUrl}
                getCategoryLabel={getCategoryLabel}
                formatDate={formatDate}
                stripHtml={stripHtml}
                loading="lazy"
                locale={locale}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
